import { WModalButton, type WModalButtonProps } from "@/components/button/WToolButton.tsx";
import { WModal, WModalContent } from "@/components/modal/WModal.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { ModalHeaderBlock } from "@/modules/compute/ModalHeaderBlock.tsx";
import { rebuildFromReplicaMutation, type VirtualMachineLoc } from "@/modules/compute/vmQuery.ts";
import { useGenerateUuid } from "@/utils/useGenerateUuid.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import type { VirtualMachineStorageReplica } from "@warrenio/api-spec/spec.oats.gen";

interface VmRestoreFromReplicaModalProps {
    vm: VirtualMachineLoc;
    replica: VirtualMachineStorageReplica;
}

function VmRestoreFromReplicaModalContent({ vm, replica }: VmRestoreFromReplicaModalProps) {
    const { t } = useLingui();
    const restoreMutation = useStandardMutation(rebuildFromReplicaMutation);
    const requestUuid = useGenerateUuid(undefined);
    const { type } = replica;

    return (
        <WModalContent
            title={type === "backup" ? t`Restore from backup` : t`Restore from snapshot`}
            label={t`Restore`}
            modalAction={async () => {
                await restoreMutation.mutateAsync({
                    location: vm.location,
                    body: {
                        replica_uuid: replica.uuid,
                        uuid: vm.uuid,
                        type: replica.type,
                    },
                    requestUuid,
                });
            }}
        >
            <p className="pb-2">
                {type === "backup" ? (
                    <Trans>Restoring this backup will overwrite the current state.</Trans>
                ) : (
                    <Trans>Restoring this snapshot will overwrite the current state.</Trans>
                )}{" "}
                <Trans comment="Explanation: Restoring a snapshot or backup will delete the current state of the virtual machine.">
                    Current changes will be discarded.
                </Trans>
            </p>

            <ModalHeaderBlock vm={vm} replica={replica} showSize />
        </WModalContent>
    );
}

export function VmRestoreFromReplicaModal({
    vm,
    replica,
    ...buttonProps
}: VmRestoreFromReplicaModalProps & Omit<WModalButtonProps, "label" | "icon">) {
    const { t } = useLingui();

    return (
        <WModal button={<WModalButton label={t`Restore`} icon="jp-return-icon" {...buttonProps} />}>
            <VmRestoreFromReplicaModalContent vm={vm} replica={replica} />
        </WModal>
    );
}
